import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchSolarData } from '../services/apiService';
import './PanelVoltagePage.css';

const PanelVoltagePage = () => {
  const navigate = useNavigate();
  const [solarData, setSolarData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchSolarData();
        setSolarData(data);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    
    // Refresh data every 5 seconds
    const interval = setInterval(() => {
      loadData();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const formatVoltageReading = (value) => {
    if (value === null || value === undefined) return '0 V';
    if (typeof value === 'number') {
      if (isNaN(value)) return '0 V';
      return `${value.toFixed(0)} V`;
    }
    const s = String(value).trim();
    if (!s) return '0 V';
    if (/v\s*$/i.test(s)) return s;
    const num = parseFloat(s.replace(/,/g, '.'));
    if (!isNaN(num)) return `${s} V`;
    return s;
  };

  const isNegative = (value) => {
    if (value === null || value === undefined) return false;
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return !isNaN(num) && num < 0;
  };

  const formatLastUpdated = (timeValue) => {
    if (timeValue === null || timeValue === undefined || timeValue === '') return '';
    const trimmed = String(timeValue).trim();
    if (!trimmed) return '';
    // Backend can send "0" or an explicit placeholder string for empty time.
    if (trimmed === '0') return '';
    if (/^00\s*0\s*0000\s*,\s*00:00:00$/.test(trimmed)) return '';

    // Parse device/manual format like: "21 3 2026,14:23:45"
    const manualSpaceCommaMatch = trimmed.match(/^(\d{1,2})\s+(\d{1,2})\s+(\d{4})\s*,\s*(\d{1,2}):(\d{2}):(\d{2})$/);
    if (manualSpaceCommaMatch) {
      const dd = Number(manualSpaceCommaMatch[1]);
      const mm = Number(manualSpaceCommaMatch[2]);
      const yyyy = Number(manualSpaceCommaMatch[3]);
      const hh = Number(manualSpaceCommaMatch[4]);
      const min = Number(manualSpaceCommaMatch[5]);
      const sec = Number(manualSpaceCommaMatch[6]);
      const date = new Date(yyyy, mm - 1, dd, hh, min, sec);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
      }
    }

    // Parse alternate manual format: "21-3-2026,14:23:45"
    const manualHyphenCommaMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})\s*,\s*(\d{1,2}):(\d{2}):(\d{2})$/);
    if (manualHyphenCommaMatch) {
      const dd = Number(manualHyphenCommaMatch[1]);
      const mm = Number(manualHyphenCommaMatch[2]);
      const yyyy = Number(manualHyphenCommaMatch[3]);
      const hh = Number(manualHyphenCommaMatch[4]);
      const min = Number(manualHyphenCommaMatch[5]);
      const sec = Number(manualHyphenCommaMatch[6]);
      const date = new Date(yyyy, mm - 1, dd, hh, min, sec);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
      }
    }

    try {
      const date = new Date(trimmed);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        });
      }
    } catch (e) { /* fall through */ }
    return trimmed;
  };

  if (loading) {
    return (
      <div className="panel-voltage-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Panel Voltage Data...</p>
        </div>
      </div>
    );
  }

  // Create array of all 18 panel voltage values
  const panelVoltages = [];
  for (let i = 1; i <= 18; i++) {
    panelVoltages.push({
      number: i,
      value: solarData?.[`panelVoltage${i}`] ?? 0
    });
  }

  return (
    <div className="panel-voltage-page">
      <div className="panel-voltage-container">
        <div className="header-section">
          <h1 className="page-title">PANEL VOLTAGE</h1>
          {solarData?.recordedAt && (
            <div className="last-updated">
              <div className="last-update">Last Updated: {formatLastUpdated(solarData.time) || '—'}</div>
            </div>
          )}
        </div>

        <div className="voltage-displays-section">
          {panelVoltages.map((pv) => (
            <div key={pv.number} className="voltage-display-item">
              <label className="voltage-label">PANEL VOLTAGE {pv.number}</label>
              <div className={`voltage-display ${isNegative(pv.value) ? 'negative-value' : ''}`}>
                {formatVoltageReading(pv.value)}
              </div>
            </div>
          ))}
        </div>

        <div className="button-section">
          <button 
            className="back-button"
            onClick={() => navigate('/')}
          >
            BACK TO DASHBOARD
          </button>
        </div>
      </div>
    </div>
  );
};

export default PanelVoltagePage;

